import type { JSX } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { addToast, Spinner } from '@heroui/react';

import { useAuth } from '~/hooks/useAuth';
import { useAcceptInvitation, useDeclineInvitation, useInvitation } from '~/hooks/useInvitations';
import { ApiError } from '~/services/api/client';
import {
  getTokenExpirationDate,
  isTokenExpired,
  parseInvitationToken,
} from '~/utils/invitationToken';

import type { OrgRole } from '~/types';

import { InvitationCard } from './InvitationCard';
import { InvitationExpired } from './InvitationExpired';
import { InvitationInvalid } from './InvitationInvalid';

type PageState = 'loading' | 'expired' | 'invalid' | 'ready' | 'accepting' | 'declining';

export function Invitation(): JSX.Element {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  const { loading: authLoading, user } = useAuth();
  const [pageState, setPageState] = useState<PageState>('loading');
  const autoAcceptTriggered = useRef(false);

  // Parse token to get invitation details
  const tokenPayload = useMemo(() => parseInvitationToken(token), [token]);
  const tokenExpiresAt = useMemo(() => getTokenExpirationDate(token), [token]);

  // Fetch invitation details (may fail if backend doesn't have GET endpoint)
  const { data: invitation, isLoading: invitationLoading } = useInvitation(id, token);

  // Mutations
  const acceptMutation = useAcceptInvitation();
  const declineMutation = useDeclineInvitation();

  // Validate token on mount
  useEffect(() => {
    if (!token || !id) {
      setPageState('invalid');
      addToast({
        description: 'Invalid invitation link',
        color: 'danger',
      });
      return;
    }

    // Check if token is malformed
    if (!tokenPayload) {
      setPageState('invalid');
      addToast({
        description: 'Invalid invitation token',
        color: 'danger',
      });
      return;
    }

    // Check if token is expired (with 60s buffer)
    if (isTokenExpired(token, 60)) {
      setPageState('expired');
      return;
    }

    // Token is valid
    setPageState('ready');
  }, [token, id, tokenPayload]);

  // Handle accept
  const handleAccept = useCallback(() => {
    if (!id || !token) return;

    // Check if user is logged in
    if (!user) {
      // Redirect to signup with return URL and pre-filled email
      const returnTo = encodeURIComponent(`/invitations/${id}?token=${token}&autoAccept=true`);
      const emailParam = tokenPayload?.email
        ? `&email=${encodeURIComponent(tokenPayload.email)}`
        : '';
      void navigate(`/login?returnTo=${returnTo}&mode=signup${emailParam}`);
      return;
    }

    setPageState('accepting');
    acceptMutation.mutate(
      { invitationId: id, token },
      {
        onSuccess: response => {
          // Get org name from invitation data
          const orgName = invitation?.organization_name || 'the organization';

          addToast({
            description: `Welcome to ${orgName}! You are now a member.`,
            color: 'success',
          });
          // Navigate to projects page with new org (TenantContext will switch orgs)
          void navigate(`/projects?org=${response.organization_id}`, { replace: true });
        },
        onError: error => {
          setPageState('ready');

          // Type-safe error handling with status codes
          if (error instanceof ApiError) {
            if (error.status === 410) {
              setPageState('expired');
              return;
            }

            if (error.status === 409) {
              addToast({
                description: 'This invitation has already been accepted.',
                color: 'warning',
              });
              void navigate('/', { replace: true });
              return;
            }

            if (error.status === 403) {
              addToast({
                description: 'This invitation was sent to a different email address.',
                color: 'danger',
              });
              return;
            }
          }

          // Fallback for non-ApiError or unhandled status codes
          addToast({
            description: error.message || 'Failed to accept invitation',
            color: 'danger',
          });
        },
      },
    );
  }, [id, token, tokenPayload, user, navigate, acceptMutation, invitation]);

  // Handle decline
  const handleDecline = useCallback(() => {
    if (!id) return;

    // Check if user is logged in (decline requires auth)
    if (!user) {
      const returnTo = encodeURIComponent(`/invitations/${id}?token=${token}`);
      const emailParam = tokenPayload?.email
        ? `&email=${encodeURIComponent(tokenPayload.email)}`
        : '';
      void navigate(`/login?returnTo=${returnTo}&mode=signup${emailParam}`);
      return;
    }

    setPageState('declining');
    declineMutation.mutate(id, {
      onSuccess: () => {
        addToast({
          description: 'Invitation declined.',
          color: 'default',
        });
        void navigate('/', { replace: true });
      },
      onError: error => {
        setPageState('ready');
        addToast({
          description: error.message || 'Failed to decline invitation',
          color: 'danger',
        });
      },
    });
  }, [id, token, tokenPayload, user, navigate, declineMutation]);

  // Auto-accept after signup if autoAccept param present
  useEffect(() => {
    const autoAccept = searchParams.get('autoAccept') === 'true';

    // Prerequisites: user logged in, page ready, not already triggered
    if (!user || pageState !== 'ready' || autoAcceptTriggered.current) {
      return;
    }

    if (autoAccept) {
      // Mark as triggered to prevent loops
      autoAcceptTriggered.current = true;

      // Remove autoAccept param from URL (clean UX)
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('autoAccept');
      setSearchParams(newParams, { replace: true });

      // Trigger accept (same as clicking button)
      handleAccept();
    }
  }, [user, pageState, searchParams, setSearchParams, handleAccept]);

  // Show loading spinner during auth check
  if (authLoading || pageState === 'loading') {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  // Show expired page
  if (pageState === 'expired') {
    return <InvitationExpired />;
  }

  // Invalid token - show error page
  if (pageState === 'invalid') {
    return <InvitationInvalid />;
  }

  // Determine display values
  // Priority: API response > token payload > defaults
  const organizationName = invitation?.organization_name ?? 'Organization';
  const inviterName = invitation?.inviter_name;
  const inviterEmail = invitation?.inviter_email;
  const role: OrgRole = (invitation?.role as OrgRole) ?? 'member';
  const expiresAt = invitation?.expires_at ?? tokenExpiresAt?.toISOString();

  return (
    <div className="flex min-h-[60vh] items-center justify-center p-4">
      <div className="w-full max-w-md">
        <h1 className="mb-6 text-center text-2xl font-semibold">You've Been Invited</h1>

        {invitationLoading && !invitation ? (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        ) : (
          <InvitationCard
            organizationName={organizationName}
            inviterName={inviterName}
            inviterEmail={inviterEmail}
            role={role}
            expiresAt={expiresAt}
            onAccept={handleAccept}
            onDecline={handleDecline}
            isAccepting={pageState === 'accepting'}
            isDeclining={pageState === 'declining'}
          />
        )}

        {!user && (
          <p className="mt-4 text-center text-sm text-default-500">
            You'll need to sign in to accept this invitation.
          </p>
        )}
      </div>
    </div>
  );
}
